import { useState } from "react"
import { GlobeAltIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { toast } from "react-toastify"
import { useLazyGetAssetsByPageUrlQuery } from "~/ui-rtk/api/assetsApi"
import {
  useTriggerHoloscannerCrawlMutation,
  useTriggerHoloscannerCrawlCheckMutation,
} from "~/ui-rtk/api/jobsApi"
import { assetToScrapedImage, type ScrapedImage } from "../types"

interface ScrapeFromUrlDialogProps {
  isOpen: boolean
  onClose: () => void
  onScraped: (images: ScrapedImage[]) => void
}

export default function ScrapeFromUrlDialog({
  isOpen,
  onClose,
  onScraped,
}: ScrapeFromUrlDialogProps) {
  const [url, setUrl] = useState("")
  const [fetchByPageUrl, { isFetching }] = useLazyGetAssetsByPageUrlQuery()
  const [triggerCrawl, { isLoading: isCrawling }] =
    useTriggerHoloscannerCrawlMutation()
  const [triggerCrawlCheck] = useTriggerHoloscannerCrawlCheckMutation()

  if (!isOpen) return null

  const busy = isFetching || isCrawling
  let submitLabel = "Add"
  if (isCrawling) {
    submitLabel = "Starting scrape…"
  } else if (isFetching) {
    submitLabel = "Looking up…"
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = url.trim()
    if (!trimmed) return
    try {
      const result = await fetchByPageUrl({ url: trimmed }).unwrap()
      if (!result.snapshot) {
        // No snapshot for this page yet — kick off an on-demand crawl. The job
        // only enqueues the request; the crawl runs out-of-band (Airflow sensor
        // ~every 2 min), so assets are not available immediately. Tell the user
        // and let them retry shortly.
        try {
          await triggerCrawl({
            requests: [{ url: trimmed, pageType: "CONTENT_PAGE" }],
          }).unwrap()
          // Kick off the server-side poller that watches for the crawl to land
          // and notifies via the Jobs inbox when assets are ready. Best-effort:
          // the crawl is already running, so don't fail the flow if this throws.
          triggerCrawlCheck({ url: trimmed })
            .unwrap()
            .catch(() => undefined)
          toast.info(
            "No snapshot yet — started scraping that page. We'll notify you when assets are ready (usually a few minutes).",
          )
          setUrl("")
          onClose()
        } catch {
          toast.error("Failed to start scraping that page")
        }
        return
      }
      if (!result.snapshot.entityId) {
        toast.info("Page snapshot has no linked product")
        return
      }
      const usable = result.assets.filter(
        a => a.status !== "FAILED" && a.syncStatus !== "FAILED",
      )
      if (usable.length === 0) {
        toast.info("No usable tagged assets found for that product")
        return
      }
      const images = usable.map(assetToScrapedImage)
      onScraped(images)
      toast.success(
        `Added ${images.length} asset${images.length > 1 ? "s" : ""} from URL`,
      )
      setUrl("")
      onClose()
    } catch (err) {
      const message =
        err && typeof err === "object" && "data" in err
          ? String((err as { data?: { message?: string } }).data?.message ?? "")
          : ""
      toast.error(message || "Failed to look up assets for URL")
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-sm overflow-y-auto py-10"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg mx-4 bg-system-gray-900 rounded-xl border border-system-gray-800 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-system-gray-800">
          <div className="flex items-center gap-2">
            <GlobeAltIcon className="w-5 h-5 text-brand" />
            <h2 className="text-lg font-semibold text-white">
              Add assets from URL
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-xs btn-circle"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <label className="block">
            <span className="text-xs text-system-gray-400">Page URL</span>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://example.com/product"
              className="input input-bordered w-full mt-1"
              autoFocus
              required
              disabled={busy}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="btn btn-ghost btn-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !url.trim()}
              className="btn btn-primary btn-sm gap-1"
            >
              {busy && <span className="loading loading-spinner loading-xs" />}
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
